import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import {
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map as rxMap,
  merge,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';

import { HttpLink } from 'apollo-angular/http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ApolloClient, ApolloLink, InMemoryCache, from, FetchResult, Operation } from '@apollo/client/core';

// Type alias for NextLink function (Operation and FetchResult are deprecated but still functional)
type NextLink = (operation: Operation) => Observable<FetchResult>;

import {
  authentication,
  environment,
  webAnalytics,
} from '../../environments/environment';
import { UserData } from '../models/user-data';
import { AuthService } from './auth.service';
import { LaunchdarklyService } from './launchdarkly.service';

type WebAnalyticsClientSource = {
  userData: UserData;
  launchDarklyFlags: { [key: string]: unknown };
};


/**
 * Service for managing Apollo GraphQL clients with authentication and feature flags.
 * Uses modern Angular patterns including inject() and signals where appropriate.
 */
@Injectable({
  providedIn: 'root',
})
export class ApolloClientService {
    constructor(
        private httpLink: HttpLink,
        private authService: AuthService,
        private launchdarklyService: LaunchdarklyService
    ){}
      /**
   * Observable of apollo client.
   */
  public apolloClient$ = combineLatest({
    userData: this.authService.userData$,
    launchDarklyFlags: this.launchdarklyService.launchDarklyFlags$,
  }).pipe(
    filter((source) => {
      console.log('[ApolloClientService] apolloClient$ filter check:', {
        isLoggedIn: source.userData.isLoggedIn,
        sessionID: source.userData.sessionID,
        userName: source.userData.userName,
      });
      return !!source.userData.isLoggedIn;
    }),
    rxMap((source) => this.createWebAnalyticsApolloClient(source)),
    filter((client) => !!client),
    shareReplay(1),
  );

  /**
   * Web analytics AppSync client — does not require login. URI is chosen immediately from
   * `environment.IsProduction` (staging vs release); LaunchDarkly may override when/if it emits.
   * Avoids waiting forever on `launchDarklyFlags$` (SDK `ready` never firing).
   */
  public webAnalyticsApolloClient$ = this.authService.userData$.pipe(
    switchMap((userData) =>
      merge(
        of(this.webAnalyticsLdFallbackFlags()),
        this.launchdarklyService.launchDarklyFlags$.pipe(
          catchError((err) => {
            console.warn(
              '[ApolloClientService] Web Analytics: LaunchDarkly flags failed, keeping environment URI',
              err,
            );
            return EMPTY;
          }),
        ),
      ).pipe(
        rxMap((launchDarklyFlags) =>
          this.createWebAnalyticsApolloClient({ userData, launchDarklyFlags }),
        ),
      ),
    ),
    filter((client) => !!client),
    shareReplay(1),
  );

  private webAnalyticsLdFallbackFlags(): { [key: string]: unknown } {
    return { staging: !environment.IsProduction };
  }

  private createWebAnalyticsApolloClient(
    source: WebAnalyticsClientSource,
  ): ApolloClient {
    const useStaging = source.launchDarklyFlags['staging'] !== false;
    const graphqlUri = useStaging
      ? webAnalytics.stagingUri
      : webAnalytics.releaseUri;
    const uriLink = this.httpLink.create({ uri: graphqlUri });
    console.log('[ApolloClientService] Creating web analytics client:', {
      uri: graphqlUri,
      stagingFlag: source.launchDarklyFlags['staging'],
      useStaging,
      sessionID: source.userData.sessionID,
    });

    const headerLink = setContext((_, context: any) => {
      const correlationId = uuid();
      const requestId = uuid();
      const sessionId = source.userData.sessionID ?? 'anonymous';

      return {
        ...context,
        'x-correlation-id': correlationId,
        'x-request-id': requestId,
        headers: {
          ...(context.headers || {}),
          'x-correlation-id': correlationId,
          'x-request-id': requestId,
          authorization: sessionId,
        },
      };
    });

    const errorLink = onError((errorResponse: any) => {
      const { error, networkError, graphQLErrors } = errorResponse;

      if (error?.status === 401 || error?.status === 403) {
        this.authService.clearSession();
        return;
      }

      const status =
        (networkError as any)?.status || (networkError as any)?.statusCode;
      if (status === 401 || status === 403) {
        this.authService.clearSession();
        return;
      }

      if (
        graphQLErrors?.some(
          (err: any) =>
            err.message?.includes('401') ||
            err.message?.includes('403') ||
            err.message?.includes('Unauthorized') ||
            err.message?.includes('not authenticated'),
        )
      ) {
        this.authService.clearSession();
      }
    });

    const loadingLink = new ApolloLink((operation: Operation, forward: NextLink) => {
      return new Observable<FetchResult>((observer) => {
        const subscription = forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (err) => observer.error(err),
          complete: () => observer.complete(),
        });
        return () => subscription.unsubscribe();
      });
    });

    const timeStartLink = new ApolloLink((operation, forward) => {
      const context = operation.getContext();
      const correlationId = context['x-correlation-id'];
      const startTime = new Date();
      console.log(`[${operation.operationName}] Sending request`, correlationId);
      operation.setContext({ start: startTime });
      return forward(operation);
    });

    const timeEndLink = new ApolloLink((operation, forward) => {
      return forward(operation).pipe(
        rxMap((data: any) => {
          const context = operation.getContext();
          const correlationId = context['x-correlation-id'];
          const duration = new Date().getTime() - context['start'].getTime();
          console.log(
            `[${operation.operationName}] took ${duration} to complete`,
            correlationId,
          );
          return data;
        }),
      );
    });

    const httpChain = from([
      errorLink,
      headerLink,
      loadingLink,
      timeStartLink,
      timeEndLink,
      uriLink,
    ]);

    return new ApolloClient({
      link: httpChain,
      cache: new InMemoryCache(),
      defaultOptions: {
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      },
    });
  }

  /**
   * Common Apollo Client configuration shared between all clients.
   * Sets up headers and error handling for GraphQL requests.
   */
  private commonClientConfiguration$ = this.authService.userData$.pipe(
    rxMap(userData => {
      /**
       * CRITICAL: Headers must be nested inside a 'headers' object.
       * Returning them as top-level context properties will NOT send them in the HTTP request.
       *
       * CORRECT:   return { headers: { 'authorization': sessionId } }
       * INCORRECT: return { 'authorization': sessionId }
       */
      const headerLink = setContext((_, context: any) => {
        const correlationId = uuid();
        const requestId = uuid();
        const sessionId = userData.sessionID || 'anonymous';

        return {
          ...context,
          headers: {
            ...(context.headers || {}),
            'x-correlation-id': correlationId,
            'x-request-id': requestId,
            'authorization': sessionId,
          }
        };
      });

      // Configure error handling.
      const errorLink = onError((errorResponse: any) => {
        const { operation, error, networkError, graphQLErrors } = errorResponse;
        const isConsumingToken = operation?.operationName === 'consumeOneTimeToken';

        if (isConsumingToken) return; // Don't clear session during token consumption

        // Check HttpErrorResponse (most common)
        if (error?.status === 401 || error?.status === 403) {
          this.authService.clearSession();
          return;
        }

        // Check networkError
        const status = (networkError as any)?.status || (networkError as any)?.statusCode;
        if (status === 401 || status === 403) {
          this.authService.clearSession();
          return;
        }

        // Check GraphQL errors
        if (graphQLErrors?.some((err: any) =>
          err.message?.includes('401') || err.message?.includes('403') ||
          err.message?.includes('Unauthorized') || err.message?.includes('not authenticated')
        )) {
          this.authService.clearSession();
        }
      });

      const timeStartLink = new ApolloLink((operation, forward) => {
        // this.loggerService.logDebug('forward start');
        const context = operation.getContext();
        const correlationId = context['x-correlation-id'];
        const startTime = new Date();
        // this.loggerService.logInformation(`[${operation.operationName}] Sending request`, correlationId);
        operation.setContext({ start: startTime });
        // this.loggerService.logDebug('forward end');
        return forward(operation);
      });

      const timeEndLink = new ApolloLink((operation, forward) => {
        // this.loggerService.logDebug('ApolloLink');
        return forward(operation).pipe(rxMap((data: any) => {
          const context = operation.getContext();
          const correlationId = context['x-correlation-id'];
          const duration = new Date().getTime() - context['start'].getTime();
          // this.loggerService.logInformation(`[${operation.operationName}] took ${duration} to complete`, correlationId);
          return data;
        }));
      });

      return {
        headerLink: headerLink,
        errorLink: errorLink,
        timeStartLink: timeStartLink,
        timeEndLink: timeEndLink,
      };
    }),
    shareReplay(1),
  );

  /**
   * Observable of apollo client for communicating with auth layer.
   * Matches early-delivery-client implementation - uses commonConfiguration with sessionID.
   */
  public authLayerClient = combineLatest({
    commonConfiguration: this.commonClientConfiguration$,
    launchDarklyFlags: this.launchdarklyService.launchDarklyFlags$,
  }).pipe(
    rxMap(source => {
      console.log('authLayerClient combineLatest source:', source);

      // Set uri based on environment from LaunchDarkly flags.
      // For development, default to staging unless explicitly set to false
      const useStaging = source.launchDarklyFlags['staging'] !== false;
      const authLayerUri = useStaging ? authentication.authLayerStagingUrl : authentication.authLayerReleaseUrl;
      const uriLink = this.httpLink.create({ uri: authLayerUri });
      console.log('[ApolloClientService] authLayerClient using:', {
        uri: authLayerUri,
        useStaging: useStaging
      });

      // Return client to subscribers.
      return new ApolloClient({
        link: from([source.commonConfiguration.errorLink, source.commonConfiguration.headerLink, source.commonConfiguration.timeStartLink, source.commonConfiguration.timeEndLink, uriLink]),
        cache: new InMemoryCache(),
      });
    }),
    filter(client => !!client),
    shareReplay(1),
  );
}
